import { yupResolver } from '@hookform/resolvers/yup';
import CloseIcon from '@mui/icons-material/Close';
import { Button, IconButton, Stack } from '@mui/material';
import { AxiosError } from 'axios';
import React, { FC, Fragment, useEffect } from 'react';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import {
    ControlledInput,
    FormGrid,
    IParams,
    ISaveReferralOrganisation,
    Page,
    Section,
    sortByName,
    StatusCode,
    Widget,
} from '../../../shared';
import { useReferralOrganisationSchema } from '../../validators';
import { useReferralOrganisation, useSaveReferralOrganisation } from '../../hooks/referral-organisation.hook';

export const ReferralOrganisationEditPage: FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { id } = useParams<keyof IParams>() as IParams;

    const form = useForm<ISaveReferralOrganisation>({
        resolver: yupResolver(useReferralOrganisationSchema()),
        mode: 'all',
    });

    const {
        fields: contacts,
        append,
        remove,
    } = useFieldArray({
        control: form.control,
        name: 'contacts',
    });

    const { data: organisation } = useReferralOrganisation(id);
    useEffect(() => {
        if (organisation) {
            form.reset({
                ...organisation,
                contacts: organisation.contacts.sort(sortByName),
            });
        }
    }, [organisation, form]);

    const { mutateAsync: saveOrganisation } = useSaveReferralOrganisation();

    const onSubmit = async (data: ISaveReferralOrganisation) => {
        try {
            await saveOrganisation({
                data: {
                    ...data,
                    contacts: data.contacts.filter((contact) => !!contact.name || !!contact.email || !!contact.phone),
                },
                messages: {
                    onUpdateSuccessMessage: t('updateReferralOrganisationSuccess'),
                    onUpdateErrorMessage: t('updateReferralOrganisationError'),
                    onCreateSuccessMessage: t('createReferralOrganisationSuccess'),
                    onCreateErrorMessage: t('createReferralOrganisationError'),
                },
            });
            navigate('/admin/referral-organisations');
        } catch (err) {
            if ((err as AxiosError)?.response?.status === StatusCode.CONFLICT) {
                form.setError('code', {
                    message: t('codeUniqErr'),
                });
            }
        }
    };

    const actions = [
        <Button onClick={() => navigate('/admin/referral-organisations')} color="secondary">
            {t('cancel')}
        </Button>,
        <Button variant="contained" color="primary" onClick={form.handleSubmit(onSubmit)}>
            {t('save')}
        </Button>,
    ];

    return (
        <Page title={organisation?.name || t('newReferralOrganisation')} actions={actions} onBack={() => navigate(-1)}>
            <FormProvider {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)}>
                    <Widget footer={[...actions].reverse()}>
                        <Stack spacing={2}>
                            <Section title={t('data')}>
                                <FormGrid size={{ xs: 3 }}>
                                    <ControlledInput name="name" label={t('nameLabel')} required={true} />
                                    <ControlledInput name="code" label={t('codeLabel')} required={true} />
                                </FormGrid>
                            </Section>

                            <Section title={t('contacts')}>
                                {contacts.map((contact, index) => (
                                    <Fragment key={contact.id}>
                                        <FormGrid size={{ xs: 3 }}>
                                            <ControlledInput name={`contacts.${index}.name`} label={t('nameLabel')} />
                                            <ControlledInput name={`contacts.${index}.email`} label={t('Email')} />
                                            <ControlledInput name={`contacts.${index}.phone`} label={t('Phone')} />
                                            <IconButton aria-label="delete" onClick={() => remove(index)} size="large">
                                                <CloseIcon />
                                            </IconButton>
                                        </FormGrid>
                                    </Fragment>
                                ))}
                                <Button data-testid="add-contact" onClick={() => append({})} color="secondary">
                                    + {t('contactsAdd')}
                                </Button>
                            </Section>
                        </Stack>
                    </Widget>
                </form>
            </FormProvider>
        </Page>
    );
};
